import {getStyleProperties} from '@packages/systems/style/utils';
import {isGrid, isLayout} from '@packages/utilities/elementTypeUtils';
import {weakMemo, cacheMemo} from '@packages/utilities/memo';

import {PSEUDO_KEYS} from '@packages/systems/core/utils/StyleUtils';
import {LARGER_BREAKPOINTS} from '@packages/systems/style/media-queries';
import type {BreakpointID, CssPropertyMap} from '@packages/systems/style/types';

import {type PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';

const elementBreakpointMemo = cacheMemo(LARGER_BREAKPOINTS.length);
const elementPseudoMemo = cacheMemo(
  PSEUDO_KEYS.length + 1 // + 1 for `null`
);
const elementVariantMemo = cacheMemo(10);

const memo__isElementGridDisplay = elementPseudoMemo(
  (pseudoMode?: PseudoStateKey | null) =>
    elementBreakpointMemo((breakpointId: BreakpointID) =>
      elementVariantMemo((variantState: VariantStateKey | null) =>
        weakMemo((cssPropertyMap: CssPropertyMap) =>
          weakMemo((element: ExpressionElement): boolean => {
            const propMap =
              getStyleProperties(breakpointId)(pseudoMode)(variantState)(
                cssPropertyMap
              )(element);

            const display = propMap.get('display');
            if (display === 'grid' || display === 'inline-grid') return true;

            if (isGrid(element) || isLayout(element)) {
              // For grid/layout atoms, assume grid display if no display property is set
              return !propMap.has('display');
            }

            return false;
          })
        )
      )
    )
);

export const isElementGridDisplay = (
  element: ExpressionElement,
  breakpointId: BreakpointID,
  pseudoMode: PseudoStateKey | null | undefined,
  variantState: VariantStateKey | null,
  cssPropertyMap: CssPropertyMap
) => {
  if (cssPropertyMap == null || element == null) return false;

  return memo__isElementGridDisplay(pseudoMode)(breakpointId)(variantState)(
    cssPropertyMap
  )(element);
};
